import { COMPUTE_CHIPS } from 'react_pages/Event/components/Charts2/ComputeForwardCurve/computeForwardCurveApi';
import { chipLabelFromKey } from 'react_pages/Event/components/Charts2/ComputeForwardCurve/computeForwardCurveData';

import type { AnyField } from '../fields';
import { COMPUTE_REQUESTS } from '../dataSource';
import type { WidgetSchema } from '../types';

/**
 * GPU compute forward curve widget.
 *
 * Reads the same forward-curve API the site chart reads, so an embed on a partner site
 * and kalshi.com cannot show different numbers.
 *
 * The chip is a user-facing picker rather than five separate widgets. It rides the
 * iframe src as `?chip=`, which `buildDirectIframeCode` forwards from any enum field
 * (see EmbedConfig.iframeParams), so the options here stay derived from COMPUTE_CHIPS
 * and a new chip needs no widget change.
 */

const chipField: AnyField = {
    kind: 'enum',
    key: 'chip',
    label: 'GPU',
    defaultValue: 'b200',
    control: 'select',
    options: COMPUTE_CHIPS.map((chip) => ({ value: chip.toLowerCase(), label: chipLabelFromKey(chip) })),
};

const utmField: AnyField = {
    kind: 'utm',
    key: 'utm',
    label: 'Marketing (UTM)',
    defaultValue: { utmSource: '', utmCampaign: '', promoCode: '' },
};

const chromeFields: AnyField[] = [
    { kind: 'boolean', key: 'showBorder', label: 'Border', defaultValue: false },
    { kind: 'boolean', key: 'rounded', label: 'Rounded corners', defaultValue: false },
];

export const computeForwardCurve: WidgetSchema = {
    meta: {
        id: 'compute-forward-curve',
        category: 'compute',
        categoryTitle: 'Compute',
        title: 'GPU Compute Forward Curve',
        description:
            "The market's expected price of GPU compute per GPU-hour, from realized spot through the weekly and monthly forward curve.",
        status: 'live',
    },
    fields: [
        chipField,
        { kind: 'theme', key: 'colorTheme', label: 'Theme', defaultValue: 'light', hidden: true },
        {
            kind: 'dimensions',
            key: 'dimensions',
            label: 'Dimensions',
            defaultValue: { width: '100%', height: '560px' },
            fixed: { width: '100%', height: '560px' },
            hidden: true,
        },
        utmField,
        ...chromeFields,
    ],
    preview: { kind: 'direct-iframe' },
    embed: { kind: 'direct-iframe', fixed: {}, iframePath: '/external-widgets/compute' },
    dataSource: {
        summary:
            'The curve is served by this app and is the same feed the kalshi.com chart, the broadcast page and Bloomberg read, so a rebuild cannot disagree with our own numbers.',
        requests: COMPUTE_REQUESTS,
        browserCallable: true,
        notes: ['Chip keys match the picker above; a new chip appears here without a widget change.'],
    },
};
